import { Collapse } from "antd";
import { type CollapseProps } from "antd/lib/collapse";
import { type FC, memo, type ReactNode } from "react";

import { BTLocalStorage } from "types/btStorage";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isNullOrWhitespace } from "utilities/string/string";

// Define a typed map for stored collapse keys
type CollapsePrefs = Record<string, CollapseProps["defaultActiveKey"]>;

export interface IBTCollapseProps extends Omit<CollapseProps, "onChange" | "panel"> {
    "data-testid": string;
    onChange?: (key: string | string[], isActive: boolean) => void;
    sessionStorageKey?: string;
    children: ReactNode;
}

export interface IBTCollapsePanelProps {
    isActive?: boolean;
}

const hasActivePanel = (key?: (string | number)[] | string | number) => {
    if (key === undefined) {
        return false;
    }
    if (Array.isArray(key)) {
        return key.length > 0;
    }
    if (typeof key === "string") {
        return !isNullOrWhitespace(key);
    }
    return !!key;
};

export const BTCollapse: FC<IBTCollapseProps & ITrackingProp> = track((props) => ({
    component: "Collapse",
    uniqueId: props["data-testid"],
}))(
    memo(
        ({
            onChange,
            sessionStorageKey,
            children,
            "data-testid": testid,
            tracking,
            defaultActiveKey,
            ...otherProps
        }) => {
            const handleChange = (key: string | string[]) => {
                onChange?.(key, hasActivePanel(key));
                tracking?.trackEvent({
                    event: "CollapseToggled",
                    extraInfo: {
                        key: key,
                        hasActivePanel: hasActivePanel(key),
                    },
                });
                if (sessionStorageKey) {
                    // Cast stored data to CollapsePrefs for type safety
                    const values = (BTLocalStorage.get("bt-object-btCollapsePrefs") ||
                        {}) as CollapsePrefs;
                    values[sessionStorageKey] = key;
                    BTLocalStorage.set("bt-object-btCollapsePrefs", values);
                }
            };

            // Retrieve and type collapse preferences
            const collapsePrefs = (BTLocalStorage.get("bt-object-btCollapsePrefs") ||
                {}) as CollapsePrefs;
            const defaultKey =
                sessionStorageKey && collapsePrefs[sessionStorageKey] !== undefined
                    ? collapsePrefs[sessionStorageKey]
                    : defaultActiveKey;

            return (
                // eslint-disable-next-line react/forbid-elements
                <Collapse
                    onChange={handleChange}
                    defaultActiveKey={defaultKey}
                    data-testid={testid}
                    {...otherProps}
                >
                    {children}
                </Collapse>
            );
        },
    ),
);

export enum BTCollapseSessionStorageKeys {
    jobFromTemplate = "jobFromTemplate",
}
